import React from "react";
import { withUserProfile } from "../../../../userProfile/userProfileContext";
import { withFeatureFlags } from "../../../../userProfile/featureFlagsContext";
import { withStyles } from "@material-ui/core";
import { compose } from "recompose";
import { getDataWithQueryString } from "../../../../api/componentAPIs";
import DataGrid from '@dataGrid';
import GridConfig from "./GridConfig";
import styles from "../../Settings/styles";
import MergeStops from "../../IBPlanning/Common/MergeStops/index";
import { withSnackbar } from "notistack";
import { MERGE_TYPES } from "../../IBPlanning/Common/MergeStops/utills";
import MergeStopAlert from "../../IBPlanning/Common/MergeStops/MergeStopAlert";
import { csvFormulaInjectionSanitizer } from "../../../../utilities/csvUtils";
import { v4 as uuidv4 } from "uuid";
import appConstants from "../../../../appConstants";
import {
  filterDuplicateCustomersByTrapRoute,
  mapCustomerToMergeFormat,
  hasSelectedTrapRouteCustomers,
  hasMultipleGroupIds,
  getDuplicateCustomerApiConfig
} from "./utils";
import DuplicateCustomersHeader from "./Header";

class DuplicateCustomers extends React.Component {
  constructor(props) {
    super(props);
    this.gridApi = null;
    this.pendingFetchAfterTerminalChange = false;
    this.state = {
      duplicateCustomerData: null,
      filteredDuplicateCustomerData: null,
      quickFilterText: "",
      selectedCustomers: [],
      showMergeCustomerPopup: false,
      showMergeCustomerAlert: false,
      excludeTrapRoute: true,
      isDuplicateCustomerTerminalLevelEnabled: this.getIsTerminalLevelEnabled(props),
      gridStatus: appConstants.GRID_STATUS.LOADING,
    };
    this.gridConfig = new GridConfig({
      featureFlags: {
        isDuplicateCustomerTerminalLevelEnabled: this.state.isDuplicateCustomerTerminalLevelEnabled
      }
    });
  }

  /**
   * Determines if terminal-level API should be used based on mode and feature flag
   * In Dynamic Routing mode, always use scenario-level APIs regardless of feature flag
   */
  getIsTerminalLevelEnabled = (props) => {
    // In Dynamic Routing context, always use scenario-level APIs regardless of feature flag
    if (props.mode === appConstants.DUPLICATE_CUSTOMER_MODES.DYNAMIC_ROUTING) {
      return false;
    }
    // In standalone mode, follow the feature flag
    return props.featureFlags?.isFeatureEnabled?.(
      appConstants.features.EnableCustomerDuplicateCheck
    ) || false;
  }

  onGridReady = ({ api }) => {
    this.gridApi = api;
  };

  updateQuickFilterText = (value) => {
    this.setState({
      quickFilterText: value,
    });
  };

  exportCSV = () => {
    if (!this.state.duplicateCustomerData?.length || !this.gridApi) {
      return;
    }
    const columnIds = this.gridApi.getColumns()
      .reduce((columnKeys, column) => {
        if (column.getColDef().headerName) {
          columnKeys.push(column.getColId());
        }
        return columnKeys;
      }, []);
    const params = {
      fileName: "Duplicate_Customer_Report",
      columnKeys: columnIds,
      processCellCallback: (cell) => csvFormulaInjectionSanitizer(cell.value)
    };
    this.gridApi.exportDataAsCsv(params);
  };
  componentDidMount() {
    this._isComponentMounted = true;
    this.fetchDuplicateCustomers();
  }

  componentWillUnmount() {
    this._isComponentMounted = false;
  }

  componentDidUpdate(prevProps) {
    this.handleDataRefresh(prevProps);
  }

  handleDataRefresh = (prevProps) => {
    const { isDuplicateCustomerTerminalLevelEnabled } = this.state;
    const terminalChanged = prevProps.userData.userProfile.terminal?.code !== this.props.userData.userProfile.terminal?.code;
    const scenarioChanged = prevProps.userData.userProfile.scenarioID !== this.props.userData.userProfile.scenarioID;
    const featureFlagsChanged = prevProps.featureFlags.featureFlags !== this.props.featureFlags.featureFlags;
    const isTerminalOrScenarioChanged = terminalChanged || scenarioChanged;
    const isDynamicRoutingMode = this.props.mode === appConstants.DUPLICATE_CUSTOMER_MODES.DYNAMIC_ROUTING;
    // Terminal or scenario changed - always wait for feature flags API to load first (unless in Dynamic Routing mode)
    if (isTerminalOrScenarioChanged && !isDynamicRoutingMode) {
      this.pendingFetchAfterTerminalChange = true;
      return;
    }

    // Feature flags loaded after terminal or scenario change - safe to fetch data now
    if (featureFlagsChanged && this.pendingFetchAfterTerminalChange) {
      this.updateFeatureFlagAndFetch();
      return;
    }

    // Fetch data if terminal or scenario changed with feature disabled, or always fetch in Dynamic Routing mode
    if (isTerminalOrScenarioChanged && (!isDuplicateCustomerTerminalLevelEnabled || isDynamicRoutingMode)) {
      this.fetchDuplicateCustomers();
      return;
    }
  }

  updateFeatureFlagAndFetch = () => {
    const currentFeatureFlag = this.getIsTerminalLevelEnabled(this.props);

    this.pendingFetchAfterTerminalChange = false;

    if (this.state.isDuplicateCustomerTerminalLevelEnabled !== currentFeatureFlag) {
      this.gridConfig = new GridConfig({
        featureFlags: { isDuplicateCustomerTerminalLevelEnabled: currentFeatureFlag }
      });
    }

    this.setState({
      isDuplicateCustomerTerminalLevelEnabled: currentFeatureFlag
    }, this.fetchDuplicateCustomers);
  }

  fetchDuplicateCustomers = () => {
    const terminal = this.props.userData?.userProfile?.terminal;
    const scenarioId = this.props.userData?.userProfile?.scenarioID;

    if (this.state.isDuplicateCustomerTerminalLevelEnabled && (!terminal?.code || !terminal?.id)) {
      return;
    }

    this.setGridStatusToLoading();

    const { endpoint, queryString } = getDuplicateCustomerApiConfig({
      isTerminalLevel: this.state.isDuplicateCustomerTerminalLevelEnabled,
      terminalId: terminal?.id,
      scenarioId
    });

    getDataWithQueryString(endpoint, queryString).then(
      (response) => {
        if (!this._isComponentMounted) return;

        if (response.status === 200) {
          this.processDuplicateCustomerData(response.data);
        } else {
          this.handleFetchError();
        }
      },
      () => {
        if (this._isComponentMounted) {
          this.handleFetchError();
        }
      }
    );
  };

  setGridStatusToLoading = () => {
    this.setState({
      duplicateCustomerData: null,
      filteredDuplicateCustomerData: null,
      gridStatus: appConstants.GRID_STATUS.LOADING,
    });
  };

  processDuplicateCustomerData = (data) => {
    let filteredData = this.state.isDuplicateCustomerTerminalLevelEnabled
      ? data
      : filterDuplicateCustomersByTrapRoute(this.state.excludeTrapRoute, data);

    if (this.props.filterCustomers) {
      filteredData = this.filterCustomersForOptimization(filteredData);
    }

    this.setState({
      duplicateCustomerData: data,
      filteredDuplicateCustomerData: filteredData,
      gridStatus: appConstants.GRID_STATUS.SUCCESS,
    });
  };

  handleFetchError = () => {
    this.props.enqueueSnackbar("Unable to fetch duplicate customers data", {
      variant: "error",
    });
    this.setState({
      duplicateCustomerData: [],
      filteredDuplicateCustomerData: [],
      gridStatus: appConstants.GRID_STATUS.ERROR,
    });
  };

  filterCustomersForOptimization = (data) => {
    return data.filter((customer) =>
      this.props.filterCustomers.includes(customer.customerID)
    );
  };

  onCancelMergeCustomers = () => {
    this.setState({ showMergeCustomerPopup: false });
  };

  onMergeCustomerCompleted = () => {
    this.fetchDuplicateCustomers();
  };

  onCancelMergeCustomerAlert = () => {
    this.setState({ showMergeCustomerAlert: false });
  };

  onConfirmMergeCustomerAlert = () => {
    this.setState({
      showMergeCustomerAlert: false,
      showMergeCustomerPopup: true,
    });
  };

  onCellFocused = (params) => {
    if (this.state.showMergeCustomerPopup) {
      this.setState({ showMergeCustomerAlert: true });
    }
  };

  handleTrapFilter = (event) => {
    const filteredData = filterDuplicateCustomersByTrapRoute(
      event.target.checked,
      this.state.duplicateCustomerData
    );
    this.setState({
      filteredDuplicateCustomerData: filteredData,
      excludeTrapRoute: event.target.checked,
    });
  };

  onSelectionChanged = () => {
    const selectedRows = this.gridApi.getSelectedRows();
    this.setState({
      selectedCustomers: selectedRows.map((customer) =>
        mapCustomerToMergeFormat(customer, uuidv4)
      ),
    });
  };

  mergeCustomers = () => {
    const { selectedCustomers } = this.state;

    if (!this.state.isDuplicateCustomerTerminalLevelEnabled && hasSelectedTrapRouteCustomers(selectedCustomers)) {
      this.props.enqueueSnackbar("Trap routes customers can not be merged.", {
        variant: "error",
      });
      return;
    }

    if (hasMultipleGroupIds(selectedCustomers)) {
      this.props.enqueueSnackbar("You can only merge customers with same address.", {
        variant: "error",
      });
      return;
    }

    this.setState({ showMergeCustomerPopup: true });
  };

  render() {
    const { classes, customClasses, disableHeaderActions } = this.props;
    const { config } = this.gridConfig;
    const enableMergeCustomerIcon = this.state.selectedCustomers.length >= 2;

    return (
      <div className={classes.duplicateCustomerSubModule}>
        <DuplicateCustomersHeader
          classes={classes}
          disableHeaderActions={disableHeaderActions}
          isDuplicateCustomerTerminalLevelEnabled={this.state.isDuplicateCustomerTerminalLevelEnabled}
          excludeTrapRoute={this.state.excludeTrapRoute}
          onTrapFilterChange={this.handleTrapFilter}
          enableMergeCustomerIcon={enableMergeCustomerIcon}
          onMergeCustomers={this.mergeCustomers}
          onSearchChange={this.updateQuickFilterText}
          onExportCSV={this.exportCSV}
          gridApi={this.gridApi}
        />
        <div className={`${classes.reportsIBPlanningGridContainer} ${customClasses?.gridContainer || ''}`}>
          <div className={`${classes.dataGrid} ${classes.anchorPointContent}`}>
            <DataGrid
              key={`duplicate-customers-grid-${this.state.isDuplicateCustomerTerminalLevelEnabled}`}
              columnDefs={config.columns}
              config={config}
              rowData={this.state.filteredDuplicateCustomerData}
              onGridReady={this.onGridReady}
              quickFilterText={this.state.quickFilterText}
              rowSelection={this.gridConfig.config.rowSelection}
              rowDeselection={this.gridConfig.config.rowDeselection}
              onSelectionChanged={this.onSelectionChanged}
              onCellMouseDown={this.onCellFocused}
              suppressRowClickSelection={this.state.showMergeCustomerPopup}
              showErrorMessage={this.state.gridStatus}
            />
          </div>
        </div>
        {this.state.showMergeCustomerPopup && (
          <MergeStops
            open
            onClose={this.onCancelMergeCustomers}
            selectedStops={this.state.selectedCustomers}
            glEventHub={this.props.glEventHub}
            mergeType={MERGE_TYPES.Customers}
            onMergeCompleted={this.onMergeCustomerCompleted}
            isDuplicateCustomerTerminalLevelEnabled={this.state.isDuplicateCustomerTerminalLevelEnabled}
          />
        )}
        {this.state.showMergeCustomerAlert && (
          <MergeStopAlert
            onCancel={this.onCancelMergeCustomerAlert}
            onConfirm={this.onConfirmMergeCustomerAlert}
            inProgress={this.state.showMergeCustomerPopup}
          />
        )}
      </div>
    );
  }
}
export default compose(
  withStyles(styles),
  withUserProfile,
  withFeatureFlags,
  withSnackbar
)(DuplicateCustomers);
